import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Container, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, CircularProgress,
  Button
} from '@mui/material';
import { ChevronLeft, ChevronRight, PlayArrow as JoinIcon } from '@mui/icons-material';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { getStudentBookings } from '../../api/booking';
import { getDateInTimezone, getTimeInTimezone } from '../../utils/timezone';

const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const STATUS_STYLE = {
  confirmed: { bg: '#E8F5E9', border: '#A5D6A7', text: '#2E7D32' },
  pending:   { bg: '#FFF8EC', border: '#F5A623', text: '#E65100' },
  completed: { bg: '#E3F2FD', border: '#90CAF9', text: '#1565C0' },
  cancelled: { bg: '#FAFAFA', border: '#e0e0e0', text: '#9e9e9e' },
};

function getMondayOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

const SchedulePage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(getMondayOfWeek(new Date()));

  useEffect(() => {
    if (!user?.id) return;
    getStudentBookings(user.id)
      .then(data => setBookings(data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [user?.id]);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  const weekDateStrs = weekDates.map(d => toLocalDateStr(d));
  const today = toLocalDateStr(new Date());

  // The student's IANA timezone — falls back to the browser's if not yet persisted.
  const studentTz = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Display helpers — prefer UTC timestamps, fall back to legacy strings.
  const bDate      = (b) => b.startAtUTC ? getDateInTimezone(b.startAtUTC, studentTz) : b.date;
  const bStartTime = (b) => b.startAtUTC ? getTimeInTimezone(b.startAtUTC, studentTz) : b.startTime;
  const bEndTime   = (b) => b.endAtUTC   ? getTimeInTimezone(b.endAtUTC,   studentTz) : b.endTime;

  const weekBookings = bookings.filter(b => weekDateStrs.includes(bDate(b)));
  const timeSlots = [...new Set(weekBookings.map(b => bStartTime(b)))].sort();

  const getBooking = (dateStr, time) =>
    weekBookings.find(b => bDate(b) === dateStr && bStartTime(b) === time) || null;

  const navigateWeek = (dir) => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + dir * 7);
      return d;
    });
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      background: '#FAFAFA',
      pt: { xs: '80px', sm: '90px' },
      pb: 4
    }}>
      <Container maxWidth="xl">
        <Typography variant="h5" fontWeight={700} color="#1a1a1a" mb={3}>
          {t('schedule.title')}
        </Typography>

        <Paper elevation={0} sx={{ border: '1px solid #eee', borderRadius: 3, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ px: 3, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a">
              {t('schedule.weeklySchedule', 'My Weekly Schedule')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton size="small" onClick={() => navigateWeek(-1)} sx={{ color: '#666' }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150, textAlign: 'center' }}>
                {weekDates[0].toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                {' – '}
                {weekDates[6].toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}
              </Typography>
              <IconButton size="small" onClick={() => navigateWeek(1)} sx={{ color: '#666' }}>
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#F5A623' }} />
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 700, tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                    <TableCell sx={{
                      width: 72, fontWeight: 700, fontSize: '0.78rem',
                      color: '#F5A623', borderBottom: '2px solid #f0f0f0', py: 1.5, pl: 2
                    }}>
                      Time
                    </TableCell>
                    {weekDates.map((d, i) => {
                      const isToday = toLocalDateStr(d) === today;
                      const dateLabel = d.toLocaleDateString('en', { day: 'numeric', month: 'short' });
                      return (
                        <TableCell key={i} align="center" sx={{
                          fontWeight: 700, fontSize: '0.82rem',
                          color: isToday ? '#F5A623' : '#333',
                          borderBottom: '2px solid #f0f0f0', py: 1.5
                        }}>
                          {DAY_SHORT[i]}
                          <Box component="span" sx={{
                            display: 'block', fontSize: '0.7rem', fontWeight: 400,
                            color: isToday ? '#F5A623' : '#888'
                          }}>
                            {dateLabel}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeSlots.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{
                        py: 6, color: '#bbb', fontSize: '0.9rem', border: 0
                      }}>
                        {t('schedule.noLessonsThisWeek', 'No lessons scheduled this week')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    timeSlots.map(time => (
                      <TableRow key={time} sx={{ '&:last-child td': { border: 0 } }}>
                        <TableCell sx={{
                          fontWeight: 700, fontSize: '0.8rem', color: '#F5A623',
                          borderBottom: '1px solid #f5f5f5', pl: 2, verticalAlign: 'top', pt: 1.5
                        }}>
                          {time}
                        </TableCell>
                        {weekDateStrs.map((dateStr, di) => {
                          const b = getBooking(dateStr, time);
                          const style = STATUS_STYLE[b?.status] || STATUS_STYLE.cancelled;
                          return (
                            <TableCell key={di} align="center" sx={{
                              borderBottom: '1px solid #f5f5f5', p: 0.8, verticalAlign: 'top'
                            }}>
                              {b && (
                                <Box sx={{
                                  bgcolor: style.bg,
                                  border: `1.5px solid ${style.border}`,
                                  borderRadius: 1.5, p: 1, textAlign: 'left'
                                }}>
                                  <Typography variant="caption" fontWeight={700}
                                    sx={{ color: style.text, display: 'block', lineHeight: 1.3 }}>
                                    {b.teacherName}
                                  </Typography>
                                  <Typography variant="caption"
                                    sx={{ color: style.text, opacity: 0.85, display: 'block' }}>
                                    {bStartTime(b)} - {bEndTime(b)}
                                  </Typography>
                                  {b.status === 'confirmed' && b.zoomLink && (
                                    <Button
                                      size="small"
                                      startIcon={<JoinIcon sx={{ fontSize: 12 }} />}
                                      onClick={() => window.open(b.zoomLink, '_blank')}
                                      sx={{
                                        mt: 0.5, py: 0, px: 0.5, minWidth: 0,
                                        fontSize: '0.68rem', fontWeight: 700,
                                        color: '#2E7D32', textTransform: 'none',
                                        '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                                      }}>
                                      Join
                                    </Button>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, px: 3, py: 1.5, borderTop: '1px solid #f5f5f5', flexWrap: 'wrap' }}>
            {[
              { label: 'Confirmed', style: STATUS_STYLE.confirmed },
              { label: 'Pending',   style: STATUS_STYLE.pending },
              { label: 'Completed', style: STATUS_STYLE.completed },
            ].map(({ label, style }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                <Box sx={{
                  width: 11, height: 11, borderRadius: '50%',
                  bgcolor: style.bg, border: `2px solid ${style.border}`
                }} />
                <Typography variant="caption" sx={{ color: style.text, fontWeight: 600 }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SchedulePage;
